import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { throwError } from 'rxjs';
import { NotificationService } from './notification.service';
import { ToastrService } from 'ngx-toastr';

@Injectable({
  providedIn: 'root',
})
export class ErrorHandlingService {
  errorLink = { alert: 0, msg: '', api: '', status: '', timestamp: '' };
  constructor(
    private router: Router,
    private readonly ngxLoader: NgxUiLoaderService,
    private notificationService: NotificationService,
    private toastr: ToastrService,
  ) {
    this.toastr.toastrConfig.preventDuplicates = true;
    this.toastr.toastrConfig.maxOpened = 1;
  }

  handleError({ error }: HttpErrorResponse, api = '', request?: HttpRequest<any>) {
    if (!error) {
      this.ngxLoader.stop();

      return throwError('');
    }

    if (this.router.url.includes('/login') || this.router.url.includes('/signup') || this.router.url.includes('/password-reset')) {
      this.ngxLoader.stop();

      return throwError(error);
    }
    switch (true) {
      case error.status === 0:
        this.showError('Unexpected application error.');
        this.ngxLoader.stop();
        break;
      case error.status === 401:
        this.showError('Logged out, please log in again');
        localStorage.clear();
        this.router.navigate(['/login']);
        localStorage.setItem('returnUrl', location.pathname);
        this.ngxLoader.stop();
        break;
      case error.status === 502:
        this.showError('Unexpected application error.');
        this.showErrorReportProblem(error.message || `Error ${error.status}`, api, error.status, error.timestamp);
        this.ngxLoader.stop();
        break;
      case error.status === 403:
        const errorMessage = error.message || `Error ${error.status}`;
        if (!errorMessage.includes('Requires ADMIN, user with team role, or organization owner')) {
          this.showErrorReportProblem(errorMessage, api, error.status, error.timestamp);
          if (!this.router.url.includes('/ui-kit')) {
            if (request.method === 'PATCH' && this.router.url.includes('/settings')) {
              return throwError({});
            } else {
              this.router.navigateByUrl('/dashboard');
            }
          }
        }
        this.ngxLoader.stop();
        break;
      case error.status >= 400 && error.status < 500:
        this.showErrorReportProblem(error.message || `Error ${error.status}`, api, error.status, error.timestamp);
        this.ngxLoader.stop();
        break;
      case error.status >= 500 && error.status < 600:
        this.showErrorReportProblem(error.message || `Error ${error.status}`, api, error.status, error.timestamp);
        this.ngxLoader.stop();
        break;
    }
    this.ngxLoader.stop();

    return throwError(error);
  }

  showError(msg: string) {
    this.toastr.error(msg);
  }

  showWarning(msg: string) {
    this.toastr.warning(msg);
  }

  showErrorReportProblem(msg: string, api: string, status: string, timestamp: string) {
    if (!msg) {
      msg = 'Unexpected application error.';
    }

    const toastRef = this.toastr.error('<a class=\"toastr-link\">Report Problem</a>', msg, {
      closeButton: true,
      enableHtml: true,
      disableTimeOut: true,
      tapToDismiss: false,
    });
    toastRef.onTap.pipe().subscribe(() => this.sendError());
    this.errorLink = { alert: toastRef.toastId, msg: msg, api: api, status: status, timestamp: timestamp };
  }

  sendError() {
    this.toastr.clear(this.errorLink.alert);
    const description = `Error: ${this.errorLink.msg} REST: ${this.errorLink.api} Status: ${this.errorLink.status} Date: ${new Date(this.errorLink.timestamp)}`;
    this.notificationService.sendErrorFeedback(description).subscribe((data) => {
      this.notificationService.showSuccess('Feedback sent successfully.');
    });
  }

  notFound(message: string, redirect = true): void {
    this.showError(message);
    if (redirect) {
      this.router.navigateByUrl('/dashboard');
    }
  }

  notAuthenticated(): void {
    localStorage.clear();
    this.router.navigate(['/login']);
    if (!this.router.url.includes('/login') && !this.router.url.includes('/signup') && !this.router.url.includes('/password-reset')) {
      localStorage.setItem('returnUrl', location.pathname);
    }
  }

  handleBlobError(blob: Blob): Promise<any> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const result = reader.result as string;
          const errorData = JSON.parse(result);
          resolve(errorData); // Resolve with parsed error data
        } catch (parseError) {
          reject(parseError); // Reject with parse error
        }
      };
      reader.onerror = () => {
        reject(reader.error); // Reject with file reading error
      };
      reader.readAsText(blob);
    });
  }
}
